/**
 * VideoPodcastItem - Display component for video podcasts with episodes
 */
import { 
  Youtube, Play, Plus, ExternalLink, X, Check, 
  Trash2, RefreshCcw, Loader2 
} from 'lucide-react';

export const VideoPodcastItem = ({ 
  podcast, 
  syncing, 
  onSync, 
  onAddEpisode, 
  onToggleActive, 
  onDelete, 
  onDeleteEpisode 
}) => {
  return (
    <div
      className={`bg-zinc-900 rounded-xl border overflow-hidden ${
        podcast.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'
      }`}
      data-testid={`video-podcast-${podcast.id}`}
    >
      {/* Podcast Header */}
      <div className="p-4 flex items-start gap-4">
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800 flex items-center justify-center">
          {podcast.image_url ? (
            <img src={podcast.image_url} alt={podcast.name} className="w-full h-full object-cover" />
          ) : (
            <Youtube className="w-8 h-8 text-red-500" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-white font-semibold">{podcast.name}</h4>
              <p className="text-zinc-500 text-sm truncate">{podcast.description || 'No description'}</p>
            </div>
            {!podcast.is_active && (
              <span className="bg-red-500/20 text-red-500 text-xs px-2 py-1 rounded">Inactive</span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
            <span className="flex items-center gap-1">
              <Play className="w-4 h-4" />
              {podcast.episode_count || 0} episodes
            </span>
            <span className="bg-zinc-800 px-2 py-0.5 rounded text-xs">{podcast.category}</span>
            <span className="bg-red-500/20 text-red-400 px-2 py-0.5 rounded text-xs capitalize">
              {podcast.youtube_type}
            </span>
            {podcast.last_synced && (
              <span className="text-zinc-600 text-xs">
                Last synced: {new Date(podcast.last_synced).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Sync button - only for playlists and channels */}
          {podcast.youtube_type !== 'video' && (
            <button
              onClick={() => onSync(podcast.id)}
              disabled={syncing}
              className="p-2 hover:bg-blue-500/20 rounded-lg transition-colors"
              title="Sync from YouTube"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
              ) : (
                <RefreshCcw className="w-4 h-4 text-blue-500" />
              )}
            </button>
          )}
          <button
            onClick={() => onAddEpisode(podcast.id)}
            className="p-2 hover:bg-green-500/20 rounded-lg transition-colors"
            title="Add episode"
          >
            <Plus className="w-4 h-4 text-green-500" />
          </button>
          <a
            href={podcast.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            title="Open on YouTube"
          >
            <ExternalLink className="w-4 h-4 text-zinc-400" />
          </a>
          <button
            onClick={() => onToggleActive(podcast.id, podcast.is_active, true)}
            className={`p-2 rounded-lg transition-colors ${
              podcast.is_active ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'
            }`}
            title={podcast.is_active ? 'Deactivate' : 'Activate'}
          >
            {podcast.is_active ? (
              <X className="w-4 h-4 text-red-500" />
            ) : (
              <Check className="w-4 h-4 text-green-500" />
            )}
          </button>
          <button
            onClick={() => onDelete(podcast.id)}
            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
            title="Delete podcast"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>

      {/* Episodes List */}
      {podcast.episodes && podcast.episodes.length > 0 && (
        <div className="border-t border-white/5 bg-black/30">
          <div className="p-3">
            <h5 className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Episodes</h5>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {podcast.episodes.map((episode) => (
                <div
                  key={episode.id}
                  className="flex items-center gap-3 p-2 bg-zinc-900/50 rounded-lg group"
                >
                  <div className="w-16 h-10 rounded overflow-hidden flex-shrink-0 bg-zinc-800">
                    <img
                      src={episode.thumbnail_url}
                      alt={episode.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{episode.title}</p>
                    <p className="text-zinc-500 text-xs">
                      {episode.published_date ? new Date(episode.published_date).toLocaleDateString() : 'No date'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a
                      href={episode.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1 hover:bg-zinc-700 rounded"
                    >
                      <ExternalLink className="w-3 h-3 text-zinc-400" />
                    </a>
                    <button
                      onClick={() => onDeleteEpisode(podcast.id, episode.id)}
                      className="p-1 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPodcastItem;
